<template>
    <div class="payment-calendar-summary-board">
        <el-row :gutter="20" class="container" type="flex">
            <el-col :sm="6">
                <el-card class="card stretch">
                    <div slot="header">
                        <div class="income card-title">應收合計</div>
                        <div class="money-block">
                            <span class="ah-inout-sign add"></span>
                            <Money :v="totalIncome" round />
                            <span class="twd-label">新台幣</span>
                        </div>
                    </div>
                    <div>
                        <div v-for="(income, i) in incomeItems" :key="`income-item-${i}`" class="item-block">
                            <div class="title-block">
                                <div class="color-dot" :style="{ 'background-color': income.color }" />
                                <div class="title-text">{{ income.title }}</div>
                                <div class="percentage">{{ income.percentage }} %</div>
                            </div>
                            <template>
                                <div class="cny-list">
                                    <div v-for="(incomeItem, i) in income.data" :key="`v-out-${i}`" class="cny-block">
                                        <div v-for="(item, j) in incomeItem.data" :key="`v-out-item-${j}`" class="v-out-item-block">
                                            <div v-if="item.amt" class="v-out-item" :class="{ 'with-label': getShowLabel(incomeItem.data, item.cny) }">
                                                <div class="label" v-if="getShowLabel(incomeItem.data, item.cny)">{{ incomeItem.label }}</div>
                                                <el-tooltip :disabled="item.cny === 'TWD'" effect="dark" :content="getFormattedTwd(item.amtTwd)">
                                                    <div>
                                                        <span class="amt">
                                                            <Money :v="item.amt" />
                                                        </span>
                                                        <span class="cny">{{ item.cny }}</span>
                                                    </div>
                                                </el-tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                    <div class="card-footer">
                        發票入帳前，以預計收款日計算，有入帳時以最後入帳日期計算。
                    </div>
                </el-card>
            </el-col>
            <el-col :sm="12">
                <el-card class="card stretch">
                    <div slot="header">
                        <div class="payout card-title">應付合計</div>
                        <div class="money-block">
                            <span class="ah-inout-sign minus"></span>
                            <Money :v="totalPayout" round />
                            <span class="twd-label">新台幣</span>
                        </div>
                    </div>
                    <div>
                        <el-row :gutter="40">
                            <!-- 請款單（實付）、人事薪資（實付） -->
                            <el-col :sm="12">
                                <div v-for="(payout, i) in payoutItems" :key="`payout-item-${i}`" class="item-block">
                                    <div class="title-block">
                                        <div class="color-dot" :style="{ 'background-color': payout.color }" />
                                        <div class="title-text">{{ payout.title }}</div>
                                        <div class="percentage">{{ payout.percentage }} %</div>
                                    </div>
                                    <template v-if="payout.title !== '財務支出'">
                                        <div class="cny-list">
                                            <div v-for="(item, i) in payout.data" :key="`payout-${i}`" class="cny-block">
                                                <div class="v-out-item-block">
                                                    <div class="v-out-item">
                                                        <div class="label"></div>
                                                        <el-tooltip v-if="item.amt" :disabled="item.cny === 'TWD'" effect="dark" :content="getFormattedTwd(item.amtTwd)">
                                                            <div>
                                                                <span class="amt">
                                                                    <Money :v="item.amt" />
                                                                </span>
                                                                <span class="cny">{{ item.cny }}</span>
                                                            </div>
                                                        </el-tooltip>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </el-col>
                            <!-- 財務支出 -->
                            <el-col :sm="12">
                                <div class="item-block">
                                    <div class="title-block">
                                        <div class="color-dot" :style="{ 'background-color': vOutItems.color }" />
                                        <div class="title-text">{{ vOutItems.title }}</div>
                                        <div class="percentage">{{ vOutItems.percentage }} %</div>
                                    </div>
                                    <div class="cny-list">
                                        <div v-for="(payoutItem, i) in vOutItems.data" :key="`v-out-${i}`" class="cny-block">
                                            <div v-for="(item, j) in payoutItem.data" :key="`v-out-item-${j}`" class="v-out-item-block">
                                                <div v-if="item.amt" class="v-out-item" :class="{ 'with-label': getShowLabel(payoutItem.data, item.cny) }">
                                                    <div class="label" v-if="getShowLabel(payoutItem.data, item.cny)">{{ payoutItem.label }}</div>
                                                    <el-tooltip :disabled="item.cny === 'TWD'" effect="dark" :content="getFormattedTwd(item.amtTwd)">
                                                        <div>
                                                            <span class="amt">
                                                                <Money :v="item.amt" />
                                                            </span>
                                                            <span class="cny">{{ item.cny }}</span>
                                                        </div>
                                                    </el-tooltip>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </el-col>
                        </el-row>
                    </div>
                    <div class="card-footer">
                        請款單實際付款前，以預計付款日計算。
                    </div>
                </el-card>
            </el-col>
            <el-col :sm="6">
                <el-card class="card" style="height: 100%;">
                    <div slot="header">
                        <div class="net-income card-title">應收應付淨額</div>
                        <div class="money-block">
                            <span class="ah-inout-sign" :class="{ add: netIncome >= 0, minus: netIncome < 0 }"></span>
                            <Money :v="netIncome" round abs />
                            <span class="twd-label">新台幣</span>
                        </div>
                    </div>
                    <div class="item-block">
                        <div class="cny-list">
                            <div class="cny-block">
                                <div class="v-out-item-block">
                                    <div class="v-out-item with-label no-label-padding">
                                        <div class="label"><span class="ah-inout-sign add"></span> 應收合計</div>
                                        <div>
                                            <span class="amt"><Money :v="totalIncome" round/></span>
                                            <span class="cny">TWD</span>
                                        </div>
                                    </div>
                                    <div class="v-out-item with-label no-label-padding">
                                        <div class="label"><span class="ah-inout-sign minus"></span> 應付合計</div>
                                        <div>
                                            <span class="amt"><Money :v="totalPayout" round/></span>
                                            <span class="cny">TWD</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </el-card>
            </el-col>
        </el-row>
    </div>
</template>

<script>
import _ from 'lodash';

export default {
    props: {
        year: {
            type: Number,
            default: 0,
        },
        month: {
            type: Number,
            default: 0,
        },
        summary: {
            type: Object,
            default: () => {},
        },
    },
    computed: {
        totalIncome() {
            return this.summary.invoiceTotal + this.summary.vInTotal;
        },
        totalPayout() {
            return this.summary.paymentTotal + this.summary.salaryTotal + this.summary.vOutTotal;
        },
        netIncome() {
            return this.totalIncome - this.totalPayout;
        },

        //
        incomeItems() {
            return [
                {
                    color: '#56C500',
                    title: '發票應收帳款（含稅）',
                    percentage: Math.round((this.summary.invoiceTotal / this.totalIncome) * 1000) / 10 || 0,
                    data: this.summary.invoiceByCny,
                },
                {
                    color: '#3C8206',
                    title: '財務收入',
                    percentage: Math.round((this.summary.vInTotal / this.totalIncome) * 1000) / 10 || 0,
                    data: this.summary.vInByCny,
                },
            ];
        },
        payoutItems() {
            return [
                {
                    color: '#F55C5C',
                    title: '請款單（實付）',
                    percentage: Math.round((this.summary.paymentTotal / this.totalPayout) * 1000) / 10 || 0,
                    data: this.summary.paymentsByCny,
                },
                {
                    color: '#FF9B3F',
                    title: '人事薪資（實付）',
                    percentage: Math.round((this.summary.salaryTotal / this.totalPayout) * 1000) / 10 || 0,
                    data: this.summary.salaryByCny,
                },
            ];
        },
        vOutItems() {
            return {
                color: '#ffd74b',
                title: '財務支出',
                percentage: Math.round((this.summary.vOutTotal / this.totalPayout) * 1000) / 10 || 0,
                data: this.summary.vOutByCny,
            };
        },
    },
    methods: {
        getShowLabel(item, cny) {
            const FIRST_CNY = _.chain(item)
                .map((o) => (o.amt ? o.cny : undefined))
                .compact()
                .first()
                .value();

            return FIRST_CNY === cny;
        },
        getFormattedTwd(num) {
            return `約當台幣：${Number(Math.round(num)).toLocaleString('en')} TWD`;
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vars.scss";
.payment-calendar-summary-board {
    padding: 0 20px;
    .container {
        padding-bottom: 20px;
    }
    .date-block .title {
        font-size: 15pt;
        margin-bottom: 10px;
    }
    .card {
        border-radius: 15px;
        margin-bottom: 15px;
        overflow: unset;

        &.stretch {
            height: 100%;
        }
        .card-title {
            font-size: 11pt;
        }
        .money-block {
            margin-top: 5px;
            display: flex;
            align-items: center;
            .ah-inout-sign {
                margin-right: 10px;
            }
            .money {
                font-size: 16pt;
            }
            .twd-label {
                position: relative;
                top: 2px;
                margin-left: 10px;
                font-size: 10pt;
            }
        }
        .income {
            color: #56c500;
        }
        .payout {
            color: #f55c5c;
        }
        .net-income {
            color: #444;
        }

        .card-footer {
            font-size: 9pt;
            color: #888;
            text-align: right;
            border-top: 1px solid #ddd;
            padding: 5px 0;
            margin-bottom: -15px;
        }
    }
    .item-block {
        margin-bottom: 10px;
        font-size: 10pt;
        .title-block {
            display: flex;
            color: #444;
            align-items: center;
            padding: 2px 8px 2px 22px;
            border-radius: 5px;
            margin-bottom: 10px;
            background-color: #f4f4f4;
            .title-text {
                flex: 1;
                font-weight: bold;
            }
        }
        .cny-list {
            display: flex;
            flex-direction: column;
            margin-bottom: 20px;
            .cny {
                margin-left: 12px;
                font-size: 8pt;
            }
            .cny-block {
                width: 100%;
                text-align: right;
                margin-bottom: 2px;
                font-size: 10pt;
            }
        }
        .v-out-item-block {
            width: 100%;
            .v-out-item {
                display: flex;
                justify-content: flex-end;
                &.with-label {
                    justify-content: space-between;
                    margin-left: 22px;

                    &.no-label-padding {
                        margin-left: 0;
                    }
                }
            }
        }
    }
}
</style>

<style lang="scss">
@import "@/assets/scss/vars.scss";
.payment-calendar-summary-board {
    .el-card__header {
        font-weight: bold;
    }
}
</style>
